import React, { useRef, useEffect, useState, useMemo } from "react";
import {
  Spinner,
  VStack,
  Text,
  Box,
  HStack,
  Divider,
} from "@chakra-ui/react";
import { SectionNode } from "./types";

interface HelpSectionProps {
  section: SectionNode;
  isDark: boolean;
  theme: any;
  targetChildId: string | null;
  searchQuery: string;
  parentTitle?: string;
}

const calloutIcons = {
  info: `<svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" style="margin-top: 2px;"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></svg>`,
  warning: `<svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" style="margin-top: 2px;"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></svg>`,
  important: `<svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" style="margin-top: 2px;"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="9" y1="9" x2="15" y2="15"></line><line x1="15" y1="9" x2="9" y2="15"></line></svg>`,
  success: `<svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" style="margin-top: 2px;"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`,
  tip: `<svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" style="margin-top: 2px;"><path d="M9 18h6"></path><path d="M10 22h4"></path><path d="M15 13a6 6 0 0 1-3 5.24V20h-0.24a6 6 0 0 1-2.76-5.24z"></path><path d="M12 2a6 6 0 0 1 6 6c0 1.89-.87 3.59-2.24 4.76A6 6 0 0 1 12 2z"></path></svg>`
};

const preProcessHtml = (html: string, isDark: boolean): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // 1. Wrap Figure captions & images
  doc.querySelectorAll("p").forEach((p) => {
    const text = p.textContent || "";
    if (/^Figure \d+/.test(text.trim())) {
      let prev = p.previousElementSibling;
      while (prev) {
        if (
          prev.querySelector("img") ||
          (prev.tagName === "H2" && prev.querySelector("img"))
        ) {
          const wrapper = doc.createElement("div");
          wrapper.className = "figure-container";
          wrapper.style.cssText = "text-align: center; margin: 24px 0;";
          prev.parentNode?.insertBefore(wrapper, prev);
          wrapper.appendChild(prev);
          wrapper.appendChild(p);
          return;
        }
        prev = prev.previousElementSibling;
      }
    }
  });

  // 2. Convert single-cell tables as callout boxes
  doc.querySelectorAll("table").forEach((table) => {
    const cells = table.querySelectorAll("td");
    if (cells.length === 1) {
      const cell = cells[0];
      const text = cell.textContent || "";
      const trimmed = text.trim();
      
      let type = "info";
      let prefixLen = 0;
      
      if (trimmed.toLowerCase().startsWith("note:")) {
        type = "info";
        prefixLen = 5;
      } else if (trimmed.toLowerCase().startsWith("warning:")) {
        type = "warning";
        prefixLen = 8;
      } else if (trimmed.toLowerCase().startsWith("important:")) {
        type = "important";
        prefixLen = 10;
      } else if (trimmed.toLowerCase().startsWith("success:")) {
        type = "success";
        prefixLen = 8;
      } else if (trimmed.toLowerCase().startsWith("tip:")) {
        type = "tip";
        prefixLen = 4;
      }

      const callout = doc.createElement("div");
      callout.className = `help-callout-card callout-${type}`;
      
      let bg = "rgba(56, 189, 248, 0.06)";
      let border = "#38bdf8";
      let textCol = isDark ? "#e0f2fe" : "#0369a1";
      
      if (type === "warning") {
        bg = "rgba(251, 191, 36, 0.06)";
        border = "#f59e0b";
        textCol = isDark ? "#fef3c7" : "#78350f";
      } else if (type === "important") {
        bg = "rgba(244, 63, 94, 0.06)";
        border = "#f43f5e";
        textCol = isDark ? "#ffe4e6" : "#9f1239";
      } else if (type === "success") {
        bg = "rgba(16, 185, 129, 0.06)";
        border = "#10b981";
        textCol = isDark ? "#ecfdf5" : "#064e3b";
      } else if (type === "tip") {
        bg = "rgba(181, 145, 67, 0.06)";
        border = "#B59143";
        textCol = isDark ? "#FBD282" : "#78350f";
      }

      callout.style.cssText = `
        display: flex;
        gap: 14px;
        padding: 18px;
        margin: 24px 0;
        border-radius: 8px;
        border: 1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"};
        border-left: 4px solid ${border};
        background-color: ${bg};
        color: ${textCol};
        font-size: 14.5px;
        line-height: 1.7;
      `;
      
      const cleanContent = prefixLen > 0 ? trimmed.substring(prefixLen).trim() : cell.innerHTML;
      callout.innerHTML = `
        <div style="flex-shrink: 0; color: ${border}">${calloutIcons[type as keyof typeof calloutIcons]}</div>
        <div style="flex: 1;">${cleanContent}</div>
      `;
      
      table.parentNode?.replaceChild(callout, table);
    }
  });

  // 3. Format remaining paragraphs starting with "Note:", "Warning:", etc. as callouts
  doc.querySelectorAll("p").forEach((p) => {
    if (p.closest(".figure-container")) return;
    if (p.className.includes("help-callout-card")) return;
    
    const text = p.textContent || "";
    const trimmed = text.trim();
    let type = "";
    let prefixLen = 0;
    
    if (trimmed.startsWith("Note:")) {
      type = "info";
      prefixLen = 5;
    } else if (trimmed.startsWith("Warning:")) {
      type = "warning";
      prefixLen = 8;
    } else if (trimmed.startsWith("Important:")) {
      type = "important";
      prefixLen = 10;
    } else if (trimmed.startsWith("Success:")) {
      type = "success";
      prefixLen = 8;
    } else if (trimmed.startsWith("Tip:")) {
      type = "tip";
      prefixLen = 4;
    }

    if (type) {
      let bg = "rgba(56, 189, 248, 0.06)";
      let border = "#38bdf8";
      let textCol = isDark ? "#e0f2fe" : "#0369a1";
      
      if (type === "warning") {
        bg = "rgba(251, 191, 36, 0.06)";
        border = "#f59e0b";
        textCol = isDark ? "#fef3c7" : "#78350f";
      } else if (type === "important") {
        bg = "rgba(244, 63, 94, 0.06)";
        border = "#f43f5e";
        textCol = isDark ? "#ffe4e6" : "#9f1239";
      } else if (type === "success") {
        bg = "rgba(16, 185, 129, 0.06)";
        border = "#10b981";
        textCol = isDark ? "#ecfdf5" : "#064e3b";
      } else if (type === "tip") {
        bg = "rgba(181, 145, 67, 0.06)";
        border = "#B59143";
        textCol = isDark ? "#FBD282" : "#78350f";
      }

      p.style.cssText = `
        display: flex;
        gap: 14px;
        padding: 16px 18px;
        margin: 20px 0;
        border-radius: 8px;
        border: 1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"};
        border-left: 4px solid ${border};
        background-color: ${bg};
        color: ${textCol};
        font-size: 14.5px;
        line-height: 1.7;
      `;
      
      const cleanContent = trimmed.substring(prefixLen).trim();
      p.innerHTML = `
        <div style="flex-shrink: 0; color: ${border}">${calloutIcons[type as keyof typeof calloutIcons]}</div>
        <div style="flex: 1;">${cleanContent}</div>
      `;
    }
  });

  // 4. Wrap code blocks with Copy buttons
  doc.querySelectorAll("pre, code").forEach((block) => {
    if (block.parentElement?.className === "code-block-container") return;
    
    const container = doc.createElement("div");
    container.className = "code-block-container";
    container.style.cssText = "position: relative; margin: 20px 0;";
    
    (block as HTMLElement).style.cssText = `
      font-family: 'Fira Code', 'Courier New', monospace;
      background-color: ${isDark ? "#14161B" : "#F3F4F6"};
      color: ${isDark ? "#E2E8F0" : "#2D3748"};
      padding: 16px;
      border-radius: 8px;
      font-size: 13.5px;
      border: 1px solid ${isDark ? "#2E2F33" : "#E2E8F0"};
      overflow-x: auto;
      display: block;
    `;
    
    const copyBtn = doc.createElement("button");
    copyBtn.className = "copy-code-btn";
    copyBtn.innerHTML = "Copy";
    copyBtn.style.cssText = `
      position: absolute;
      top: 8px;
      right: 8px;
      background: ${isDark ? "#1E2024" : "#FFFFFF"};
      border: 1px solid ${isDark ? "#2E2F33" : "#CBD5E0"};
      color: ${isDark ? "#C3C9D3" : "#4A5568"};
      padding: 4px 8px;
      font-size: 11px;
      border-radius: 4px;
      cursor: pointer;
      opacity: 0.7;
      transition: opacity 0.2s ease, background 0.2s ease;
    `;
    
    block.parentNode?.insertBefore(container, block);
    container.appendChild(block);
    container.appendChild(copyBtn);
  });

  // 5. Wrap remaining tables in horizontal scroll containers
  doc.querySelectorAll("table").forEach((table) => {
    const tbl = table as HTMLElement;
    if (tbl.parentElement?.className !== "table-responsive-wrapper") {
      const wrapper = doc.createElement("div");
      wrapper.className = "table-responsive-wrapper";
      tbl.parentNode?.insertBefore(wrapper, tbl);
      wrapper.appendChild(tbl);
    }
  });

  return doc.body.innerHTML;
};

const highlightHtmlString = (html: string, query: string): string => {
  if (!query || query.trim() === "") return html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  
  const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const reg = new RegExp(`(${escapeRegExp(query)})`, "gi");
  
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue || "";
      if (reg.test(text)) {
        const span = doc.createElement("span");
        span.innerHTML = text.replace(
          reg,
          `<mark style="background-color: rgba(181, 145, 67, 0.25); color: #FBD282; padding-left: 2px; padding-right: 2px; border-bottom: 1px solid #B59143; border-radius: 2px; font-weight: 600;">$1</mark>`
        );
        node.parentNode?.replaceChild(span, node);
      }
    } else {
      const name = node.nodeName.toLowerCase();
      if (name !== "script" && name !== "style" && name !== "mark" && name !== "button" && name !== "a") {
        Array.from(node.childNodes).forEach(walk);
      }
    }
  };
  
  walk(doc.body);
  return doc.body.innerHTML;
};

const HelpSection: React.FC<HelpSectionProps> = ({
  section,
  isDark,
  theme,
  targetChildId,
  searchQuery,
  parentTitle,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [toc, setToc] = useState<{ id: string; text: string; level: number }[]>([]);
  const [activeTocId, setActiveTocId] = useState<string>("");
  const [readingTime, setReadingTime] = useState(1);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Debounce search query changes to prevent jank on rapid keystrokes
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 180);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Step 1: Pre-process raw Google Doc HTML string (run ONLY on section load)
  const processedHtml = useMemo(() => {
    if (!section.content) return "";
    return preProcessHtml(section.content, isDark);
  }, [section.content, isDark]);

  // Step 2: Dynamically inject search term highlights (run ONLY on debounced search changes)
  const finalHtml = useMemo(() => {
    return highlightHtmlString(processedHtml, debouncedSearchQuery);
  }, [processedHtml, debouncedSearchQuery]);

  // Step 3: Compute reading time
  useEffect(() => {
    if (!section.content) return;
    const words = section.content.replace(/<[^>]*>/g, "").trim().split(/\s+/).filter(Boolean).length;
    setReadingTime(Math.max(1, Math.ceil(words / 200)));
  }, [section.content]);

  // Step 4: Add non-destructive listeners (onerror, hover events, copy triggers, scroll navigation)
  useEffect(() => {
    if (!contentRef.current || !section.loaded) return;
    const container = contentRef.current;

    // 1. Copy Buttons click binder
    container.querySelectorAll(".copy-code-btn").forEach((btn) => {
      const button = btn as HTMLButtonElement;
      
      // Make button visible on code block hover via CSS logic in <style>
      const handleCopy = () => {
        const codeBlock = button.parentElement?.querySelector("pre, code");
        if (codeBlock) {
          navigator.clipboard.writeText(codeBlock.textContent || "");
          button.innerHTML = "Copied!";
          setTimeout(() => {
            button.innerHTML = "Copy";
          }, 2000);
        }
      };

      button.addEventListener("click", handleCopy);
      return () => button.removeEventListener("click", handleCopy);
    });

    // 2. Image error fallback binder
    container.querySelectorAll(".help-article-img").forEach((imgNode) => {
      const img = imgNode as HTMLImageElement;
      img.onerror = () => {
        const altText = img.alt || "Documentation Screenshot";
        const placeholder = document.createElement("div");
        placeholder.style.cssText = `
          padding: 24px;
          background: ${isDark ? "#14161B" : "#F9FAFB"};
          border: 1px dashed ${isDark ? "#2E2F33" : "#E2E8F0"};
          border-radius: 8px;
          text-align: center;
          color: ${isDark ? "#718096" : "#A0AEC0"};
          font-size: 13px;
          margin: 16px 0;
        `;
        placeholder.innerHTML = `
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" style="margin: 0 auto 8px auto; opacity: 0.6;"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
          <div>${altText}</div>
        `;
        img.parentNode?.replaceChild(placeholder, img);
      };
    });

  }, [finalHtml, section.loaded, isDark]);

  // Step 5: Table of Contents generator & ScrollSpy Observer (run ONLY when processedHtml loads)
  useEffect(() => {
    if (!contentRef.current || !section.loaded) return;
    const container = contentRef.current;

    // Build Table of Contents dynamically
    const headings = Array.from(container.querySelectorAll("h2, h3"));
    const extractedToc = headings.map((heading, index) => {
      if (!heading.id) {
        heading.id = `heading-${index}-${heading.textContent?.toLowerCase().replace(/[^a-z0-9]+/g, "-") || ""}`;
      }
      (heading as HTMLElement).style.scrollMarginTop = "48px";
      return {
        id: heading.id,
        text: heading.textContent || "",
        level: heading.tagName === "H2" ? 2 : 3,
      };
    });
    setToc(extractedToc);

    // Scroll Spy Observer to highlight current active TOC section
    const observerOptions = {
      root: container.parentElement,
      rootMargin: "0px 0px -55% 0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveTocId(entry.target.id);
        }
      });
    }, observerOptions);

    headings.forEach((heading) => observer.observe(heading));

    // Scroll immediately to targetChildId if provided, else scroll to top
    if (targetChildId) {
      setTimeout(() => {
        const el = document.getElementById(targetChildId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      container.parentElement?.scrollTo({ top: 0 });
    }

    return () => {
      observer.disconnect();
    };
  }, [processedHtml, section.loaded, targetChildId]);

  if (section.loading && !section.loaded) {
    return (
      <VStack py={24} spacing={4} flex={1} justify="center">
        <Spinner size="xl" color="zs.accent" />
        <Text color={theme.textMuted} fontSize="sm">Loading article content...</Text>
      </VStack>
    );
  }

  if (!section.loaded || !section.content) {
    return (
      <VStack py={24} spacing={4} flex={1} justify="center">
        <Text color={theme.textMuted} fontSize="sm">Article content not found.</Text>
      </VStack>
    );
  }

  const handleTocClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Step 6: Define static CSS stylesheet rules (runs natively at browser speed)
  const customStyles = `
    .help-html-content {
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
    }
    
    /* Headings */
    .help-html-content h1:first-of-type {
      color: ${isDark ? "#FBD282" : "#B59143"};
      font-size: 2.25rem;
      font-weight: 800;
      margin-top: 0;
      margin-bottom: 1.25rem;
      border-bottom: none;
      padding-bottom: 0;
      letter-spacing: -0.025em;
      line-height: 1.25;
    }
    .help-html-content h1:not(:first-of-type) {
      color: ${isDark ? "#FBD282" : "#B59143"};
      font-size: 1.6rem;
      margin-top: 2.75rem;
      margin-bottom: 1.25rem;
      font-weight: 700;
      border-bottom: 1px solid ${isDark ? "#1E2024" : "#E2E8F0"};
      padding-bottom: 0.5rem;
      letter-spacing: -0.02em;
    }
    .help-html-content h2 {
      color: ${isDark ? "#E2E8F0" : "#2D3748"};
      font-size: 1.3rem;
      margin-top: 2.5rem;
      margin-bottom: 1rem;
      font-weight: 600;
      letter-spacing: -0.015em;
    }
    .help-html-content h3 {
      color: ${isDark ? "#C3C9D3" : "#4A5568"};
      font-size: 1.15rem;
      margin-top: 1.75rem;
      margin-bottom: 0.75rem;
      font-weight: 600;
    }
    
    /* Body & Paragraphs */
    .help-html-content p {
      margin-bottom: 1.5rem;
      line-height: 1.8;
      font-size: 15px;
      color: ${isDark ? "#C3C9D3" : "#4A5568"};
    }
    
    /* Lists */
    .help-html-content ul, .help-html-content ol {
      margin-left: 2rem;
      margin-top: 0.75rem;
      margin-bottom: 1.5rem;
      padding-left: 0.5rem;
    }
    .help-html-content li {
      margin-bottom: 0.6rem;
      line-height: 1.8;
      color: ${isDark ? "#C3C9D3" : "#4A5568"};
    }
    .help-html-content ul > li {
      list-style-type: disc;
    }
    .help-html-content ol > li {
      list-style-type: decimal;
    }
    
    /* Code Blocks */
    .code-block-container {
      position: relative;
      margin: 24px 0;
      border-radius: 8px;
      overflow: hidden;
    }
    .code-block-container:hover .copy-code-btn {
      opacity: 1;
    }
    .help-html-content pre, .help-html-content code {
      font-family: 'Fira Code', 'Courier New', monospace;
      background-color: ${isDark ? "#14161B" : "#F3F4F6"};
      color: ${isDark ? "#E2E8F0" : "#2D3748"};
      padding: 16px;
      border-radius: 8px;
      font-size: 13px;
      border: 1px solid ${isDark ? "#1E2024" : "#E2E8F0"};
      overflow-x: auto;
      display: block;
      margin: 0;
    }
    
    /* Inline code formatting */
    .help-html-content span[style*="Courier New"],
    .help-html-content span[style*="Courier"],
    .help-html-content span[style*="monospace"] {
      font-family: 'Fira Code', 'Courier New', monospace !important;
      background-color: ${isDark ? "#14161B" : "#F3F4F6"} !important;
      color: ${isDark ? "#FBD282" : "#B59143"} !important;
      padding: 2px 6px !important;
      border-radius: 4px !important;
      font-size: 0.9em !important;
      border: 1px solid ${isDark ? "#2E2F33" : "#E2E8F0"} !important;
    }
    
    /* Tables */
    .table-responsive-wrapper {
      width: 100%;
      overflow-x: auto;
      border-radius: 8px;
      margin: 24px 0;
      border: 1px solid ${isDark ? "#2E2F33" : "#E2E8F0"};
    }
    .help-html-content table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13.5px;
    }
    .help-html-content tr {
      border-bottom: 1px solid ${isDark ? "#2E2F33" : "#E2E8F0"};
      background-color: ${isDark ? "#14161B" : "#F9FAFB"};
    }
    .help-html-content tr:nth-child(even) {
      background-color: ${isDark ? "#0B0B0F" : "#FFFFFF"};
    }
    .help-html-content tr:hover {
      background-color: ${isDark ? "rgba(181, 145, 67, 0.04)" : "rgba(181, 145, 67, 0.02)"} !important;
    }
    .help-html-content td, .help-html-content th {
      padding: 12px 16px;
      border-right: 1px solid ${isDark ? "#1E2024" : "#F3F4F6"};
      border-bottom: 1px solid ${isDark ? "#1E2024" : "#F3F4F6"};
      vertical-align: middle;
    }
    .help-html-content tr:first-of-type {
      background: ${isDark ? "#14161B" : "#F9FAFB"} !important;
      font-weight: 600;
    }
    .help-html-content tr:first-of-type td, .help-html-content tr:first-of-type th {
      color: ${isDark ? "#FBD282" : "#B59143"} !important;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      padding: 14px 16px;
      border-bottom: 2px solid ${isDark ? "#2E2F33" : "#E2E8F0"};
      border-right: 1px solid ${isDark ? "#1E2024" : "#F3F4F6"};
    }
    
    /* Images */
    .help-html-content img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 16px auto;
      display: block;
      border: 1px solid ${isDark ? "#2E2F33" : "#E5E7EB"};
      box-shadow: ${isDark ? "0 4px 12px rgba(0,0,0,0.5)" : "0 4px 12px rgba(0,0,0,0.06)"};
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      cursor: pointer;
    }
    .help-html-content img:hover {
      transform: translateY(-4px);
      box-shadow: ${isDark ? "0 12px 24px rgba(0,0,0,0.6), 0 0 0 1px rgba(251,210,130,0.15)" : "0 12px 24px rgba(0,0,0,0.12)"};
    }
  `;

  return (
    <HStack align="start" spacing={10} w="100%" h="100%">
      {/* Injecting CSS stylesheet block */}
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />

      {/* Article Content Body */}
      <Box flex={1} maxW="760px" pb={24} pr={{ base: 0, xl: 8 }}>
        {/* Breadcrumb Navigation */}
        <HStack spacing={1.5} mb={3.5} fontSize="xs" color={isDark ? "#718096" : "#A0AEC0"} fontWeight="500">
          <Text _hover={{ color: "#B59143", cursor: "pointer" }}>Help</Text>
          <Text>/</Text>
          {parentTitle && (
            <>
              <Text _hover={{ color: "#B59143", cursor: "pointer" }}>{parentTitle}</Text>
              <Text>/</Text>
            </>
          )}
          <Text color={isDark ? "#C3C9D3" : "#4A5568"}>{section.title}</Text>
        </HStack>

        <Divider mb={8} borderColor={isDark ? "#1E2024" : "#EDF2F7"} />

        {/* Rendered HTML Container */}
        <div
          ref={contentRef}
          className="help-html-content"
          dangerouslySetInnerHTML={{ __html: finalHtml }}
        />
      </Box>

      {/* Table of Contents Column */}
      {toc.length > 0 && (
        <Box
          w="200px"
          minW="200px"
          display={{ base: "none", xl: "block" }}
          position="sticky"
          top="6px"
          maxH="calc(100vh - 200px)"
          overflowY="auto"
          pl={5}
          borderLeft="1px solid"
          borderColor={isDark ? "#1E2024" : "#EDF2F7"}
          sx={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" }
          }}
        >
          <Text
            fontSize="10px"
            fontWeight="700"
            textTransform="uppercase"
            letterSpacing="0.08em"
            color={isDark ? "#52525B" : "#A0AEC0"}
            mb={4}
          >
            On this page
          </Text>
          <VStack align="stretch" spacing={3}>
            {toc.map((item) => {
              const active = activeTocId === item.id;
              return (
                <Text
                  key={item.id}
                  fontSize="12px"
                  fontWeight={active ? "600" : "400"}
                  color={
                    active
                      ? isDark
                        ? "#FBD282"
                        : "#B59143"
                      : isDark
                      ? "#718096"
                      : "#718096"
                  }
                  cursor="pointer"
                  transition="all 0.15s ease"
                  _hover={{ color: isDark ? "#FBD282" : "#B59143" }}
                  pl={item.level === 3 ? 3 : 0}
                  onClick={() => handleTocClick(item.id)}
                  noOfLines={2}
                  lineHeight="1.4"
                >
                  {item.text}
                </Text>
              );
            })}
          </VStack>
        </Box>
      )}
    </HStack>
  );
};

export default HelpSection;
